import { getCartSectionsToRender, getCartObject, cartActions, replaceEscapedCharacters } from 'utils';

import RequestQueue from '~/utils/request-queue';

/**
 * Addon Cart Service
 * Centralized service for handling addon cart operations with proper error handling and optimization
 */
export class AddonCartService {
  static CONFIG = {
    INPUT_NAME: 'properties[add-on]',
    PROPERTY_NAMES: {
      ADDON: '_addon',
      CONNECTED: '_connected',
      PARENT_PRODUCT_KEY: '_parentProductKey',
      PARENT_PRODUCT_ID: '_parentProductId',
      GROUP_KEY: '_groupKey',
      TYPE: '_type',
      ADD_ON_PRODUCT_ADDED: '_add-on-product-added'
    },
    ADDON_TYPE: 'addon'
  };

  constructor() {
    this._requestQueue = new RequestQueue();
    this._cartCache = null;
    this._cacheTimestamp = null;
    this._cacheTimeout = 1000; // 1 second cache
  }

  /**
   * Get current cart data with caching
   * @returns {Promise<Object>} Cart data
   */
  getCartData() {
    const now = Date.now();

    // Return cached data if fresh
    if (this._cartCache && this._cacheTimestamp && (now - this._cacheTimestamp) < this._cacheTimeout) {
      return this._cartCache;
    }

    try {
      const cartItems = document.querySelector('cart-items');
      const cartObject = getCartObject(cartItems);

      // Update cache
      this._cartCache = cartObject;
      this._cacheTimestamp = now;

      return cartObject;
    } catch (error) {
      console.error('AddonCartService: Error getting cart data', error);
      throw new Error('Failed to retrieve cart data');
    }
  }

  /**
   * Clear cart cache
   */
  clearCache() {
    this._cartCache = null;
    this._cacheTimestamp = null;
  }

  /**
   * Find item in cart by key
   * @param {Object} cartData - Cart data
   * @param {string} itemKey - Item key
   * @returns {Object|null} Item or null if not found
   */
  findItemByKey(cartData, itemKey) {
    return cartData.items.find(item => item.key === itemKey) || null;
  }

  /**
   * Find addon item in cart
   * @param {Object} cartData - Cart data
   * @param {string} addonVariantId - Addon variant ID
   * @param {string} groupKey - Group key
   * @returns {Object|null} Addon item or null if not found
   */
  findAddonItem(cartData, addonVariantId, groupKey) {
    const { PROPERTY_NAMES, ADDON_TYPE } = AddonCartService.CONFIG;

    return cartData.items.find(item =>
      item.variant_id.toString() === addonVariantId.toString() &&
      item.properties?.[PROPERTY_NAMES.TYPE] === ADDON_TYPE &&
      item.properties?.[PROPERTY_NAMES.GROUP_KEY] === groupKey
    ) || null;
  }

  /**
   * Parse and validate addon IDs from properties
   * @param {string} addonProperty - Addon property value
   * @returns {Array<string>} Array of addon IDs
   */
  parseAddonIds(addonProperty) {
    if (!addonProperty) return [];

    return addonProperty
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
  }

  /**
   * Get existing addon IDs from parent properties
   * @param {Object} parentProperties - Parent item properties
   * @returns {Array<string>} Array of existing addon IDs
   */
  getExistingAddonIds(parentProperties) {
    const { PROPERTY_NAMES } = AddonCartService.CONFIG;
    const addonProperty = parentProperties?.[PROPERTY_NAMES.ADDON] || '';
    return this.parseAddonIds(addonProperty);
  }

  /**
   * Parse parent properties from form with error handling
   * @param {HTMLElement|Object} source - Form element or properties object
   * @returns {Object} Parsed parent properties
   */
  parseParentProperties(source) {

    if (!source) return {};
    console.log('typeof source', typeof source);
    console.log('source', source);

    // If it's already an object, return it
    if (typeof source === 'object' && !source.tagName) {
      return this._processProperties(source);
    }

    // If it's a form element, look for script element
    if (source.tagName) {
      const scriptElement = source.querySelector('script[data-addon-parent-line-item-properties]');
      if (!scriptElement) {
        console.warn('AddonCartService: Parent properties script not found');
        return {};
      }

      try {
        const properties = JSON.parse(scriptElement.textContent);
        return this._processProperties(Object.fromEntries(properties));
      } catch (error) {
        console.error('AddonCartService: Error parsing parent properties', error);
        return {};
      }
    }

    return {};
  }

  /**
   * Process properties to handle escaped characters
   * @param {Object} properties - Properties object
   * @returns {Object} Processed properties
   * @private
   */
  _processProperties(properties) {
    const processed = {};

    for (const [key, value] of properties) {
      if (typeof value === 'string') {
        processed[key] = replaceEscapedCharacters(value);
      } else {
        processed[key] = value;
      }
    }

    console.log('processed', processed);

    return processed;
  }

  /**
   * Generate unique group key
   * @returns {string} Unique group key
   */
  generateGroupKey() {
    return `group_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Create addon properties for cart item
   * @param {string} parentProductKey - Parent product key
   * @param {string} parentProductId - Parent product ID
   * @param {string} groupKey - Group key for connection
   * @returns {Object} Addon item properties
   */
  createAddonProperties(parentProductKey, parentProductId, groupKey) {
    const { PROPERTY_NAMES, ADDON_TYPE } = AddonCartService.CONFIG;

    return {
      [PROPERTY_NAMES.CONNECTED]: 'true',
      [PROPERTY_NAMES.PARENT_PRODUCT_KEY]: parentProductKey,
      [PROPERTY_NAMES.PARENT_PRODUCT_ID]: parentProductId,
      [PROPERTY_NAMES.GROUP_KEY]: groupKey,
      [PROPERTY_NAMES.TYPE]: ADDON_TYPE
    };
  }

  /**
   * Update parent properties with new addon
   * @param {Object} currentProperties - Current parent properties
   * @param {Object} newProperties - New properties from form
   * @param {string} addonId - Addon ID to add
   * @returns {Object} Updated parent properties
   */
  updateParentPropertiesForAdd(currentProperties, newProperties, addonId) {
    const { PROPERTY_NAMES } = AddonCartService.CONFIG;

    const existingAddonIds = this.getExistingAddonIds(currentProperties);

    // Add new addon ID if not already present
    if (!existingAddonIds.includes(addonId)) {
      existingAddonIds.push(addonId);
    }

    // Merge properties, prioritizing new properties but keeping existing ones
    return {
      ...currentProperties,
      ...newProperties,
      [PROPERTY_NAMES.ADDON]: existingAddonIds.join(','),
      [PROPERTY_NAMES.ADD_ON_PRODUCT_ADDED]: 'true'
    };
  }

  /**
   * Update parent properties for addon removal
   * @param {Object} currentProperties - Current parent properties
   * @param {string} addonId - Addon ID to remove
   * @returns {Object} Updated parent properties
   */
  updateParentPropertiesForRemove(currentProperties, addonId) {
    const { PROPERTY_NAMES } = AddonCartService.CONFIG;

    const existingAddonIds = this.getExistingAddonIds(currentProperties);
    const updatedAddonIds = existingAddonIds.filter(id => id !== addonId);

    const updatedProperties = { ...currentProperties };

    if (updatedAddonIds.length === 0) {
      // Remove addon-related properties if no addons left
      delete updatedProperties[PROPERTY_NAMES.ADDON];
      delete updatedProperties[PROPERTY_NAMES.ADD_ON_PRODUCT_ADDED];
    } else {
      updatedProperties[PROPERTY_NAMES.ADDON] = updatedAddonIds.join(',');
    }

    return updatedProperties;
  }

  /**
   * Prepare cart operations for adding addon
   * @param {Object} params - Parameters object
   * @returns {Promise<Object>} Cart operations
   */
  async prepareAddOperations(params) {
    const { addonId, parentProductKey, parentProductId, parentProperties, addonForm } = params;

    if (!addonId || !parentProductKey) {
      throw new Error('Missing required parameters: addonId and parentProductKey');
    }

    const cartData = this.getCartData();

    const parentItem = this.findItemByKey(cartData, parentProductKey);
    if (!parentItem) {
      throw new Error('Parent product not found in cart');
    }

    // Parse parent properties
    const newParentProperties = this.parseParentProperties(parentProperties || addonForm);

    const currentParentProperties = parentItem.properties || {};

    // Generate or use existing group key
    const { PROPERTY_NAMES } = AddonCartService.CONFIG;
    const groupKey = currentParentProperties[PROPERTY_NAMES.GROUP_KEY] ||
      newParentProperties[PROPERTY_NAMES.GROUP_KEY] ||
      this.generateGroupKey();

    // Update parent properties
    const updatedParentProperties = this.updateParentPropertiesForAdd(
      currentParentProperties,
      newParentProperties,
      addonId
    );
    updatedParentProperties[PROPERTY_NAMES.GROUP_KEY] = groupKey;

    // Create addon properties
    const addonProperties = this.createAddonProperties(
      parentProductKey,
      parentProductId,
      groupKey
    );

    return {
      updates: {
        [parentProductKey]: 0  // Remove current parent item
      },
      additions: [
        {
          id: parentItem.variant_id,
          quantity: parentItem.quantity,
          properties: updatedParentProperties
        },
        {
          id: addonId,
          quantity: 1,
          parent_id: parentItem.variant_id,
          properties: addonProperties
        }
      ]
    };
  }

  /**
   * Prepare cart operations for removing addon
   * @param {Object} params - Parameters object
   * @returns {Promise<Object>} Cart operations
   */
  async prepareRemoveOperations(params) {
    const { addonVariantId, parentProductKey } = params;

    if (!addonVariantId || !parentProductKey) {
      throw new Error('Missing required parameters: addonVariantId and parentProductKey');
    }

    const cartData = this.getCartData();

    const parentItem = this.findItemByKey(cartData, parentProductKey);
    if (!parentItem) {
      throw new Error('Parent product not found in cart');
    }

    const { PROPERTY_NAMES } = AddonCartService.CONFIG;
    const groupKey = parentItem.properties?.[PROPERTY_NAMES.GROUP_KEY];
    if (!groupKey) {
      throw new Error('Group key not found in parent properties');
    }

    // Find addon item to remove
    const addonItem = this.findAddonItem(cartData, addonVariantId, groupKey);
    if (!addonItem) {
      throw new Error('Addon item not found in cart');
    }

    // Update parent properties to remove this addon
    const updatedParentProperties = this.updateParentPropertiesForRemove(
      parentItem.properties,
      addonVariantId
    );

    return {
      updates: {
        [addonItem.key]: 0  // Remove addon item
      },
      changes: {
        id: parentItem.key,
        properties: updatedParentProperties,
        quantity: parentItem.quantity
      }
    };
  }

  /**
   * Execute cart operations using request queue
   * @param {Object} operations - Cart operations
   * @param {Function} callback - Callback function
   */
  executeCartOperations(operations, callback) {
    const { updates, additions, changes } = operations;

    if (!updates && !additions && !changes) {
      throw new Error('No cart operations provided');
    }

    const sectionsToRender = getCartSectionsToRender().map(section => section.section);
    const sectionsUrl = window.location.pathname;

    // Create action functions
    const updateAction = async () => {
      const response = await cartActions.update(
        JSON.stringify({
          updates,
          sections: sectionsToRender,
          sections_url: sectionsUrl
        })
      );
      return response;
    };

    const addAction = async () => {
      const response = await cartActions.add(
        JSON.stringify({
          items: additions,
          sections: sectionsToRender,
          sections_url: sectionsUrl
        })
      );
      return response;
    };

    const changeAction = async () => {
      const response = await cartActions.change(
        JSON.stringify({
          ...changes,
          sections: sectionsToRender,
          sections_url: sectionsUrl
        })
      );
      return response;
    };

    // Callback wrapper for request queue
    const requestQueueCallback = async (response) => {
      try {
        const state = await response.text();
        this.clearCache(); // Clear cache after successful operation
        callback(state);
      } catch (error) {
        console.error('AddonCartService: Error processing cart response', error);
        throw error;
      }
    };

    // Execute operations based on what's provided
    try {
      if (updates) {
        this._requestQueue.enqueue(updateAction);
      }

      if (additions) {
        this._requestQueue.enqueue(addAction, requestQueueCallback);
      } else if (changes) {
        this._requestQueue.enqueue(changeAction, requestQueueCallback);
      } else if (updates) {
        // Just updates, still need callback
        this._requestQueue.enqueue(updateAction, requestQueueCallback);
      }
    } catch (error) {
      console.error('AddonCartService: Error executing cart operations', error);
      throw new Error('Failed to execute cart operations');
    }
  }

  /**
   * Check if addon is already added to cart
   * @param {string} addonVariantId - Addon variant ID
   * @param {string} groupKey - Group key
   * @returns {Promise<boolean>} Whether addon is added
   */
  isAddonAdded(addonVariantId, groupKey) {
    try {
      const cartData = this.getCartData();
      const addonItem = this.findAddonItem(cartData, addonVariantId, groupKey);
      return !!addonItem;
    } catch (error) {
      console.error('AddonCartService: Error checking addon status', error);
      return false;
    }
  }

  /**
   * Get all addons for a parent product
   * @param {string} parentProductKey - Parent product key
   * @returns {Promise<Array>} Array of addon items
   */
  getAddonsForParent(parentProductKey) {
    try {
      const cartData = this.getCartData();
      const parentItem = this.findItemByKey(cartData, parentProductKey);

      if (!parentItem) {
        return [];
      }

      const { PROPERTY_NAMES } = AddonCartService.CONFIG;
      const groupKey = parentItem.properties?.[PROPERTY_NAMES.GROUP_KEY];

      if (!groupKey) {
        return [];
      }

      return cartData.items.filter(item =>
        item.properties?.[PROPERTY_NAMES.TYPE] === AddonCartService.CONFIG.ADDON_TYPE &&
        item.properties?.[PROPERTY_NAMES.GROUP_KEY] === groupKey
      );
    } catch (error) {
      console.error('AddonCartService: Error getting addons for parent', error);
      return [];
    }
  }
}